import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { AgentRequestApiService } from '../../../pages/private/tracking-demo/services/agent-request-api.service';
import { AGENT_JOIN_HANDOFF_PREFIX, REP_JOIN_HANDOFF_PREFIX } from '../../../pages/private/tracking-demo/agent-request.models';
import type { AgentJoinHandoff, JoinTicket, RepJoinHandoff } from '../../../pages/private/tracking-demo/agent-request.models';
import { ToasterService } from '../../../core/services/toaster.service';

/**
 * The "a live demo needs you" modal, mounted once in the private layout.
 *
 * It lives in `shared/` rather than under `tracking-demo/` because it belongs to the whole CRM:
 * an agent goes online on the Tracking Demo page and then carries on working elsewhere, and the
 * demo has to find them wherever they are.
 *
 * The component holds no state of its own beyond the countdown. Whether anything is ringing —
 * and whether to poll at all — is decided by {@link AgentRequestApiService}, which the presence
 * toggle writes to. So this file renders, and nothing more.
 */
@Component({
  selector: 'app-agent-request-inbox',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (invite(); as offer) {
      <div class="fixed inset-0 z-[9999] flex items-end justify-end p-4 sm:p-6 pointer-events-none">
        <div
          role="alertdialog"
          aria-labelledby="agent-request-title"
          class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border
                 border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 shadow-2xl"
        >
          <!-- countdown -->
          <div class="h-1 w-full bg-gray-100 dark:bg-white/10">
            <div
              class="h-full bg-emerald-500 transition-[width] duration-1000 ease-linear"
              [style.width.%]="remainingPercent()"
            ></div>
          </div>

          <div class="p-4">
            <div class="flex items-start gap-3">
              <span class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full
                           bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-5 w-5">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07
                           19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72
                           12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27
                           a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </span>

              <div class="min-w-0 flex-1">
                <!-- The person reading this IS the consultant, so speak to them, not about them. -->
                <h2 id="agent-request-title" class="text-sm font-semibold text-[#141437] dark:text-white">
                  A customer would like to speak with you
                </h2>
                <p class="mt-0.5 truncate text-xs text-gray-500 dark:text-ink-soft/70">
                  {{ offer.businessName || 'Credee demo' }}
                  @if (offer.hostName) { <span>· hosted by {{ offer.hostName }}</span> }
                </p>
                <p class="mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  {{ secondsLeft() }}s to respond
                </p>
              </div>
            </div>

            <div class="mt-4 flex gap-2">
              <button
                type="button"
                (click)="reject()"
                [disabled]="busy()"
                class="flex-1 rounded-lg border border-[#EAEAEA] dark:border-line px-3 py-2
                       text-xs font-medium text-gray-600 dark:text-ink-soft transition-colors
                       hover:bg-gray-50 dark:hover:bg-line disabled:opacity-60"
              >
                Decline
              </button>
              <button
                type="button"
                (click)="accept()"
                [disabled]="busy()"
                class="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white
                       transition-colors hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-wait"
              >
                {{ busy() ? 'Joining…' : 'Accept & join' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class AgentRequestInboxComponent implements OnInit, OnDestroy {
  private readonly agentRequests = inject(AgentRequestApiService);
  private readonly toaster = inject(ToasterService);

  readonly invite = this.agentRequests.invite;
  readonly busy = signal(false);

  /** Milliseconds left on the current offer, ticked once a second. */
  private readonly remainingMs = signal(0);
  /** What `remainingMs` was when this offer first appeared — the bar's 100% mark. */
  private readonly totalMs = signal(0);
  private ticker: ReturnType<typeof setInterval> | null = null;

  /** The ringtone, and the hard stop that guarantees it cannot outlive the offer. */
  private ring: HTMLAudioElement | null = null;
  private ringStop: ReturnType<typeof setTimeout> | null = null;
  private static readonly RING_SRC = 'assets/audio/notify.mp3';

  readonly secondsLeft = computed(() => Math.max(0, Math.ceil(this.remainingMs() / 1000)));

  /**
   * The bar drains against the offer's own length. The browser is never told "ten seconds" — it
   * is told "expires at 12:04:07" — so changing `LIVE_CALL_AGENT_OFFER_SECONDS` needs no rebuild.
   *
   * Measured from when we first saw the offer rather than from its `notifiedAt`: a poll can
   * arrive up to three seconds late, and a bar that starts at 70% looks broken.
   */
  readonly remainingPercent = computed(() => {
    const total = this.totalMs();
    return total > 0 ? Math.max(0, Math.min(100, (this.remainingMs() / total) * 100)) : 0;
  });

  /** Which offer we have already reacted to (rung for, sized the bar against). */
  private ringingId: number | null = null;

  constructor() {
    effect(() => {
      const offer = this.invite();
      this.busy.set(false);

      // The poll writes a NEW object every three seconds for the SAME offer, so this effect fires
      // again and again while one demo is ringing. Only a genuinely different request is a new
      // offer — otherwise the ringtone would restart every three seconds, and the countdown bar
      // would re-measure itself against the time it has LEFT and so sit at 100% forever.
      if (offer?.requestId === this.ringingId) return;
      this.ringingId = offer?.requestId ?? null;

      const left = offer ? new Date(offer.expiresAt).getTime() - Date.now() : 0;
      this.remainingMs.set(left);
      this.totalMs.set(left);

      if (offer) this.startRing(left); else this.stopRing();
    });
  }

  /**
   * Ring for exactly as long as the offer stands.
   *
   * The clip runs ~30 seconds and the window is ten, so the length of the SOUND is not what bounds
   * this — the OFFER is. It stops the instant the modal does (accepted, declined, taken by another
   * consultant, or lapsed), and the timer below is the hard stop for the one case the modal cannot
   * cover: a poll arriving late would otherwise leave the phone ringing after the demo moved on.
   *
   * Nothing here is hard-coded to ten seconds. The window comes from the offer's own `expiresAt`,
   * so changing LIVE_CALL_AGENT_OFFER_SECONDS on the server changes the ring with it.
   */
  private startRing(offerMs: number): void {
    this.stopRing();
    const el = new Audio(AgentRequestInboxComponent.RING_SRC);
    el.loop = true; // the window could outgrow the clip one day; a phone that goes quiet is worse
    this.ring = el;
    // The consultant switched THEMSELVES online in this tab, which is the gesture browsers want
    // before they allow sound. If autoplay is refused anyway, let it go: the modal is on screen
    // regardless, and a silent ring is not worth an error.
    void el.play().catch(() => undefined);
    this.ringStop = setTimeout(() => this.stopRing(), Math.max(0, offerMs));
  }

  private stopRing(): void {
    if (this.ringStop) { clearTimeout(this.ringStop); this.ringStop = null; }
    if (!this.ring) return;
    this.ring.pause();
    this.ring.currentTime = 0;
    this.ring = null;
  }

  ngOnInit(): void {
    void this.agentRequests.boot();
    this.ticker = setInterval(() => {
      const offer = this.invite();
      this.remainingMs.set(offer ? new Date(offer.expiresAt).getTime() - Date.now() : 0);
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.ticker) clearInterval(this.ticker);
    this.stopRing();
  }

  async accept(): Promise<void> {
    const offer = this.invite();
    if (!offer || this.busy()) return;
    this.stopRing(); // the click is the answer — silence it now, not when the request lands
    this.busy.set(true);
    // Open the destination tab NOW, inside the click gesture, and redirect it once the ticket
    // lands. The accept round-trip can take a few seconds (slow db), by which point a fresh
    // window.open() would be blocked as a non-user popup — the consultant would accept but never
    // actually land in the call. Placeholder text keeps the blank tab from looking broken.
    const tab = window.open('', '_blank');
    tab?.document.write(
      '<!doctype html><title>Joining…</title><body style="margin:0;font:15px system-ui,sans-serif;' +
      'display:grid;place-items:center;height:100vh;color:#5f666d">Joining the meeting…</body>');
    try {
      const ticket = await this.agentRequests.accept(offer.requestId);
      this.openMeeting(ticket, tab);
    } catch {
      tab?.close();
      // Somebody else took it, or the ten seconds ran out mid-click. Not an error worth alarming
      // anyone about — the offer is simply gone.
      this.toaster.info('That demo is no longer available.', 'Consultant request');
    } finally {
      this.busy.set(false);
    }
  }

  async reject(): Promise<void> {
    const offer = this.invite();
    if (!offer) return;
    this.stopRing();
    try {
      await this.agentRequests.reject(offer.requestId);
    } catch {
      // The offer has already moved on; the modal is closed either way.
    }
  }

  /**
   * Hand the seat to a new tab.
   *
   * The identity cannot simply ride the URL: `sessionStorage` is per-tab and `noopener` gives the
   * new tab an empty one. So it goes into `localStorage` under a one-shot nonce, and the bridge
   * page at `/credee-demo/agent-join` moves it across before entering the ordinary guest flow.
   */
  private openMeeting(ticket: JoinTicket, tab: Window | null): void {
    // A LiveKit seat carries a real join token — take the LiveKit call page instead of the mesh
    // bridge. Everything else about the accept flow is identical (transport-agnostic ring).
    if (ticket.token && ticket.wsUrl && ticket.identity) {
      this.openLiveKitMeeting(ticket, tab);
      return;
    }

    const nonce = crypto.randomUUID();
    const handoff: AgentJoinHandoff = {
      roomSessionId: ticket.roomSessionId,
      participantId: ticket.participantId,
      name: ticket.displayName,
      product: ticket.product,
    };

    try {
      localStorage.setItem(`${AGENT_JOIN_HANDOFF_PREFIX}:${nonce}`, JSON.stringify(handoff));
    } catch {
      tab?.close();
      this.toaster.error('Your browser blocked local storage, which this join needs.', 'Consultant request');
      return;
    }

    this.sendTabTo(tab, `/instant-demo/agent-join?sid=${encodeURIComponent(ticket.roomSessionId)}&handoff=${nonce}`);
  }

  /**
   * LiveKit seat: stash the pre-minted grant one-shot and open the LiveKit call page as a rep.
   * Product is derived from the room name (`<product>-<shortid>`, minted by livecall_agent), and
   * both room + product also ride the URL so the deck stage renders.
   */
  private openLiveKitMeeting(ticket: JoinTicket, tab: Window | null): void {
    const room = ticket.roomSessionId;
    const product = room.includes('-') ? room.slice(0, room.lastIndexOf('-')) : room;
    const nonce = crypto.randomUUID();
    const handoff: RepJoinHandoff = {
      token: ticket.token!,
      ws_url: ticket.wsUrl!,
      room,
      product,
      identity: ticket.identity!,
      name: ticket.displayName,
    };

    try {
      localStorage.setItem(`${REP_JOIN_HANDOFF_PREFIX}:${nonce}`, JSON.stringify(handoff));
    } catch {
      tab?.close();
      this.toaster.error('Your browser blocked local storage, which this join needs.', 'Consultant request');
      return;
    }

    this.sendTabTo(
      tab,
      `/instant-demo-v2/call?room=${encodeURIComponent(room)}` +
      `&product=${encodeURIComponent(product)}&repHandoff=${nonce}`,
    );
  }

  /**
   * Point the pre-opened tab at the join URL (absolute, so it resolves against the CRM origin, not
   * the tab's about:blank). If the popup was blocked even at click time — rare — fall back to a
   * fresh window.open so the join is never silently lost.
   */
  private sendTabTo(tab: Window | null, path: string): void {
    const url = path.startsWith('http') ? path : `${location.origin}${path}`;
    if (tab && !tab.closed) {
      tab.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
  }
}
